
import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { AuthProvider } from '@/contexts/AuthContext';
import { AcademicYearProvider } from '@/contexts/AcademicYearContext';
import Layout from '@/components/Layout/Layout';
import LoadingSpinner from '@/components/UI/LoadingSpinner';

// Lazy loading de páginas para mejor performance
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const AcademicYearsPage = lazy(() => import('@/pages/AcademicYearsPage'));
const AcademicYearDetailPage = lazy(() => import('@/pages/AcademicYearDetailPage'));
const CreateAcademicYearPage = lazy(() => import('@/pages/CreateAcademicYearPage'));

// Páginas del módulo de estudiantes
const StudentsPage = lazy(() => import('@/pages/StudentsPage'));
const CreateStudentPage = lazy(() => import('@/pages/CreateStudentPage'));
const StudentDetailPage = lazy(() => import('@/pages/StudentDetailPage'));
const StudentProfilePage = lazy(() => import('@/pages/StudentProfilePage'));
const StudentProfileEditPage = lazy(() => import('@/pages/StudentProfileEditPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const PersonalPage = lazy(() => import('@/pages/PersonalPage'));

// Páginas del módulo de docentes y calificaciones
const TeachersPage = lazy(() => import('@/pages/TeachersPage'));
const CreateTeacherPage = lazy(() => import('@/pages/CreateTeacherPage'));
const TeacherDetailPage = lazy(() => import('@/pages/TeacherDetailPage'));
const EditTeacherPage = lazy(() => import('@/pages/EditTeacherPage'));
const SubjectsPage = lazy(() => import('@/pages/SubjectsPage'));
const MyClassesPage = lazy(() => import('@/pages/MyClassesPage'));
const TeacherStudentsPage = lazy(() => import('@/pages/TeacherStudentsPage'));
const GradeEntryPage = lazy(() => import('@/pages/GradeEntryPage'));
const GradesPage = lazy(() => import('@/pages/GradesPage'));
const SuperAdminGradeEntryPage = lazy(() => import('@/pages/SuperAdminGradeEntryPage'));
const SuperAdminStudentPromotionPage = lazy(() => import('@/pages/SuperAdminStudentPromotionPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const AnecdotalRecordsPage = lazy(() => import('@/pages/AnecdotalRecordsPage'));
const InstitutionalActivitiesPage = lazy(() => import('@/pages/InstitutionalActivitiesPage'));
const FollowUpBySectionPage = lazy(() => import('@/pages/FollowUpBySectionPage'));
const FollowUpGradesPage = lazy(() => import('@/pages/FollowUpGradesPage'));
const PromotionStatusPage = lazy(() => import('@/pages/PromotionStatusPage'));
const StudentRankingPage = lazy(() => import('@/pages/StudentRankingPage'));
const OfficialBulletinPage = lazy(() => import('@/pages/OfficialBulletinPage'));
const OfficialBulletinA4VerticalPage = lazy(() => import('@/pages/OfficialBulletinA4VerticalPage'));
const PrintBulletinPage = lazy(() => import('@/pages/PrintBulletinPage'));

const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

/**
 * Componente principal de la aplicación
 * Maneja el enrutamiento y la estructura general
 */
function App() {
  return (
    <AuthProvider>
      <AcademicYearProvider>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Ruta pública - Login */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin/login" element={<LoginPage isAdmin={true} />} />

            {/* Rutas protegidas */}
            <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="academic-years" element={<AcademicYearsPage />} />
              <Route path="academic-years/create" element={<CreateAcademicYearPage />} />
              <Route path="academic-years/:id" element={<AcademicYearDetailPage />} />

              {/* Rutas del módulo de estudiantes */}
              <Route path="students" element={<StudentsPage />} />
              <Route path="students/create" element={<CreateStudentPage />} />
              <Route path="students/:studentId" element={<StudentDetailPage />} />
              <Route path="students/:studentId/profile" element={<StudentProfilePage />} />
              <Route path="students/:studentId/profile/edit" element={<StudentProfileEditPage />} />

              {/* Configuración */}
              <Route path="settings" element={<SettingsPage />} />
              <Route path="settings/personal" element={<PersonalPage />} />

              {/* Rutas del módulo de docentes */}
              <Route path="teachers" element={<TeachersPage />} />
              <Route path="teachers/create" element={<CreateTeacherPage />} />
              <Route path="teachers/:id" element={<TeacherDetailPage />} />
              <Route path="teachers/:id/edit" element={<EditTeacherPage />} />

              {/* Rutas del módulo de asignaturas */}
              <Route path="subjects" element={<SubjectsPage />} />

              {/* Rutas del módulo de clases (para docentes) */}
              <Route path="my-classes" element={<MyClassesPage />} />
              <Route path="teacher-students" element={<TeacherStudentsPage />} />
              <Route path="grade-entry/:sectionId/:schoolYearId" element={<GradeEntryPage />} />

              {/* Rutas del módulo de calificaciones */}
              <Route path="grades" element={<GradesPage />} />
              <Route path="superadmin/grade-entry" element={<SuperAdminGradeEntryPage />} />
              <Route path="superadmin/grade-entry/:sectionId/:schoolYearId" element={<SuperAdminGradeEntryPage />} />
              <Route path="superadmin/student-promotion" element={<SuperAdminStudentPromotionPage />} />

              {/* Rutas de reportes/boletines */}
              <Route path="reports" element={<ReportsPage />} />

              {/* Rutas de registros anecdóticos */}
              <Route path="anecdotal-records" element={<AnecdotalRecordsPage />} />
              <Route path="institutional-activities" element={<InstitutionalActivitiesPage />} />

              {/* Seguimiento por grado/sección (COORDINATOR, PSYCHOLOGY) */}
              <Route path="follow-up" element={<FollowUpBySectionPage />} />
              <Route path="follow-up-grades" element={<FollowUpGradesPage />} />
              <Route path="promotion-status" element={<PromotionStatusPage />} />
              <Route path="student-ranking" element={<StudentRankingPage />} />

              {/* Boletín oficial (PDF individual y bulk vía /reports) */}
              <Route path="bulletins/student/:enrollmentId" element={<OfficialBulletinA4VerticalPage />} />
              <Route path="bulletin/official/a4-vertical/:enrollmentId" element={<OfficialBulletinA4VerticalPage />} />
              <Route path="bulletin/official/a4-horizontal/:enrollmentId" element={<OfficialBulletinPage />} />

            </Route>

            {/* Ruta print-only (sin autenticación para Playwright) */}
            <Route path="print/bulletins/:enrollmentId" element={<PrintBulletinPage />} />

            {/* Ruta 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </AcademicYearProvider>
    </AuthProvider>
  );
}

export default App;
